/* ============================================================
 * Quién puede entrar al hub. Solo Aleph.
 *
 * Google demuestra que alguien es dueño de un correo; esta pantalla decide
 * si ese correo opera algo, y qué. Son dos cosas distintas: dar de alta a
 * una persona aquí no toca nada en Google, y quitarle el acceso tampoco
 * afecta a su cuenta.
 *
 * Un correo de sede entra directo a su centro, sin elegir nada. Uno de
 * Aleph ve la red entera.
 * ============================================================ */

import React, { useState } from 'react';
import { useCtx, useRecurso, useAccion, api } from '../estado.jsx';
import { Recurso } from './Comunes.jsx';

export function AccesosView() {
  const { session } = useCtx();
  const accesos = useRecurso(() => api.accesos(), []);
  const sedes = useRecurso(() => api.sedes(), []);
  const { ejecutar, enCurso, error, setError } = useAccion();

  const [email, setEmail] = useState('');
  const [rol, setRol] = useState('sede');
  const [sedeId, setSedeId] = useState('');

  const dar = () => {
    if (!/^[^\s@]+@gmail\.com$/.test(email.trim().toLowerCase())) {
      setError('El acceso debe ser una cuenta @gmail.com.');
      return;
    }
    if (rol === 'sede' && !sedeId) { setError('Elige a qué centro pertenece.'); return; }
    ejecutar(
      () => api.crearAcceso({ email: email.trim(), rol, sedeId: rol === 'sede' ? sedeId : undefined }),
      () => { setEmail(''); setSedeId(''); },
    );
  };

  // Un centro admite varios correos, así que el desplegable los ofrece todos.
  // Lo que interesa saber al elegir es cuántos tiene ya, no si está libre.
  const activosPorSede = (accesos.datos || [])
    .filter((a) => a.rol === 'sede' && a.activo)
    .reduce((cuenta, a) => ({ ...cuenta, [a.sede_id]: (cuenta[a.sede_id] || 0) + 1 }), {});

  const sedeActiva = (id) => (sedes.datos || []).find((s) => s.id === id)?.activa;

  // El servidor rechaza retirar el último acceso de un centro abierto: el
  // centro seguiría aceptando reservas sin nadie que validara los pases. Se
  // enseña aquí, como ya se hace con el acceso propio, para no ofrecer un
  // botón que solo puede acabar en error.
  const esUltimo = (a) => a.rol === 'sede' && a.activo
    && activosPorSede[a.sede_id] === 1 && sedeActiva(a.sede_id);

  return (
    <div className="view on">
      <div className="h-eyebrow">Aleph · Administración</div>
      <h1 className="h-title">Quién puede <em>entrar</em></h1>
      <p className="h-sub">
        Cada correo autorizado entra con su cuenta de Google. El de un centro va
        directo a su tablero; el de Aleph ve toda la red.
      </p>

      <h2 className="section-h">Autorizar una cuenta</h2>
      <div className="oferta-form bono-form">
        <div className="field" style={{ margin: 0 }}><label>Correo de Google</label>
          <input className="inp" type="email" value={email}
                 onChange={(e) => setEmail(e.target.value)}
                 placeholder="centro@gmail.com" /></div>

        <div className="field" style={{ margin: 0 }}><label>Acceso</label>
          <select className="inp" value={rol} onChange={(e) => setRol(e.target.value)}>
            <option value="sede">Un centro</option>
            <option value="aleph">Toda la red (Aleph)</option>
          </select></div>

        {rol === 'sede' ? (
          <div className="field" style={{ margin: 0 }}><label>Centro</label>
            <select className="inp" value={sedeId} onChange={(e) => setSedeId(e.target.value)}>
              <option value="">Elige…</option>
              {(sedes.datos || []).map((s) => {
                const n = activosPorSede[s.id] || 0;
                return (
                  <option key={s.id} value={s.id}>
                    {s.comuna} · {s.nombre} ({n === 1 ? '1 correo' : `${n} correos`})
                  </option>
                );
              })}
            </select></div>
        ) : <div className="field" style={{ margin: 0 }} />}

        <button className="btn" onClick={dar} disabled={enCurso}>
          {enCurso ? 'Autorizando…' : 'Autorizar'}
        </button>
      </div>

      <div className="inline-err show">{error}</div>

      <h2 className="section-h">Cuentas autorizadas</h2>
      <Recurso estado={accesos} que="los accesos">
        {(lista) => (
          <div className="card"><table className="stable">
            <thead><tr><th>Correo</th><th>Alcance</th><th>Estado</th><th /></tr></thead>
            <tbody>{lista.map((a) => (
              <tr key={a.id} style={a.activo ? undefined : { opacity: 0.5 }}>
                <td>
                  {a.email}
                  {a.email === session.mail
                    ? <span className="bono-tag" style={{ marginLeft: 8 }}>tú</span> : null}
                </td>
                <td>{a.rol === 'aleph'
                  ? <b>Toda la red</b>
                  : (
                    <>
                      {a.comuna} <span style={{ color: 'var(--warm-gray)' }}>· {a.sede}</span>
                      {a.activo ? (
                        <span className="bono-tag" style={{ marginLeft: 8 }}>
                          {activosPorSede[a.sede_id] === 1
                            ? '1 correo' : `${activosPorSede[a.sede_id]} correos`}
                        </span>
                      ) : null}
                    </>
                  )}</td>
                <td>{a.activo
                  ? <span className="pill">Activo</span>
                  : <span style={{ color: 'var(--err)' }}>Retirado</span>}</td>
                <td className="num">
                  {a.email === session.mail ? (
                    <span style={{ color: 'var(--warm-gray)', fontSize: 13 }}>—</span>
                  ) : esUltimo(a) ? (
                    <span style={{ color: 'var(--warm-gray)', fontSize: 13 }}
                          title={`Es el único acceso de ${a.comuna} y el centro sigue abierto. `
                                 + 'Autoriza antes otro correo, o da el centro de baja.'}>
                      Único
                    </span>
                  ) : (
                    <button className="tlink" disabled={enCurso}
                            onClick={() => ejecutar(() => api.cambiarAcceso(a.id, !a.activo))}>
                      {a.activo ? 'Retirar' : 'Reactivar'}
                    </button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Recurso>

      <p className="section-note" style={{ marginTop: 18 }}>
        Retirar un acceso no borra el registro: queda desactivado, para que se
        pueda saber quién tuvo entrada y hasta cuándo. Un centro abierto no se
        queda sin ninguno: para relevar a quien lo opera, autoriza primero el
        correo nuevo y retira después el anterior.
      </p>
    </div>
  );
}
